import { type ShelfCounts, SITES } from "@mfa/v5-contracts";
import { type CSSProperties, type KeyboardEvent, type ReactNode, useId, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styles from "./TemplateFilters.module.css";
import {
  clearFilters,
  type FilterOption,
  type TemplateFilters as Filters,
  type FromFilter,
  hasActiveFilters,
  type InTeamFilter,
  type KindFilter,
  SITES_IN_ORDER,
  type SortOrder,
  toggleValue,
} from "./template-filters";

/**
 * The one template filter bar both sites render (workflows/V5-Template-Organization.md
 * Rev 2 §R2.2 "Filter bar"; workflows/V5-Template-Method-Review.md V1, V4, V8, V9): Search;
 * From (both sites or one home site); All · Teams · Single agents, with Any · In a team ·
 * Standalone under Single agents; Category (single choice: the topics); Industry,
 * Collections (the authored keywords, Original among them) and Capabilities (derived from
 * agent.yaml), each multi-select (the template-method review, D1 and D6); "Showing N of M",
 * the order, and Clear filters. Every value shows its count against the other active
 * filters; a value at 0 is greyed and refuses a click but stays in the tab order, so MFA and
 * LYA draw the same controls and a keyboard reaches all of them.
 *
 * The single-choice rows are radio groups (one tab stop, arrows move and choose); the
 * multi-select rows are toggle buttons. Under ~720 px the rows fold into a drawer behind a
 * Filters button; the search box stays out. The state itself lives in the URL (TemplatesPage).
 */

const ink = "var(--brand-ink)";
const card = "var(--brand-card)";

const chipSty = (on: boolean, empty: boolean, tone: "ink" | "violet"): CSSProperties => ({
  padding: "6px 14px",
  border: `var(--brand-bw-2-5) solid ${ink}`,
  cursor: empty ? "not-allowed" : "pointer",
  borderRadius: "var(--brand-r-999)",
  fontFamily: "var(--brand-font-display)",
  fontWeight: 800,
  fontSize: "13px",
  boxShadow: empty ? "none" : "var(--brand-shadow-2-5)",
  background: on ? (tone === "violet" ? "var(--brand-violet)" : ink) : card,
  color: on ? card : ink,
  opacity: empty ? 0.45 : 1,
});

const rowLabelSty: CSSProperties = {
  flex: "none",
  minWidth: "84px",
  fontFamily: "var(--brand-font-display)",
  fontWeight: 800,
  fontSize: "12px",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--brand-muted-2)",
};

const smallButtonSty: CSSProperties = {
  padding: "4px 12px",
  background: "transparent",
  border: `var(--brand-bw-2) solid ${ink}`,
  borderRadius: "var(--brand-r-999)",
  cursor: "pointer",
  fontFamily: "var(--brand-font-display)",
  fontWeight: 800,
  fontSize: "12px",
  color: ink,
};

function Chip({
  on,
  count,
  tone = "ink",
  legacy = false,
  radio = false,
  onClick,
  children,
}: {
  on: boolean;
  count?: number;
  tone?: "ink" | "violet";
  legacy?: boolean;
  /** One of a radio group: checked instead of pressed, one tab stop per group. */
  radio?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  // A value with no matches stays visible and focusable but inert, unless it is the active
  // one: then a click must still be able to turn it off.
  const empty = count === 0 && !on;
  return (
    <button
      type="button"
      {...(radio
        ? { role: "radio", "aria-checked": on, tabIndex: on ? 0 : -1 }
        : { "aria-pressed": on })}
      aria-disabled={empty ? true : undefined}
      data-count={count}
      // Hooks for the gallery baselines (web/e2e/helpers/gallery-shell.css): counts, the
      // greyed state and library-era chips follow the content, so a screenshot hides them.
      data-filter-chip=""
      data-empty={count === 0 ? "" : undefined}
      data-legacy={legacy ? "" : undefined}
      style={chipSty(on, count === 0, tone)}
      onClick={() => {
        if (!empty) onClick();
      }}
    >
      {children}
      {count === undefined ? null : (
        <span data-filter-count="" style={{ fontWeight: 700 }}>{` (${count})`}</span>
      )}
    </button>
  );
}

const rowSty: CSSProperties = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  alignItems: "center",
  marginTop: "12px",
};

/** A multi-select row: a named group of toggle buttons. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a fieldset would restyle the chip row; role=group names it for assistive tech and tests
    <div role="group" aria-label={label} style={rowSty}>
      <span aria-hidden="true" style={rowLabelSty}>
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * A single-choice row: a radio group with one tab stop (the checked value). Arrow keys,
 * Home and End move between the values and choose the one they land on, unless it has no
 * matches; then it only takes focus.
 */
function RadioRow({ label, children }: { label: string; children: ReactNode }) {
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const radios = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')];
    const at = radios.indexOf(document.activeElement as HTMLButtonElement);
    if (at < 0) return;
    const last = radios.length - 1;
    const to =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? at === last
          ? 0
          : at + 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? at === 0
            ? last
            : at - 1
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? last
              : null;
    if (to === null) return;
    event.preventDefault();
    const target = radios[to];
    if (!target) return;
    target.focus();
    if (target.getAttribute("aria-disabled") !== "true") target.click();
  };
  return (
    <div role="radiogroup" aria-label={label} style={rowSty} onKeyDown={onKeyDown}>
      <span aria-hidden="true" style={rowLabelSty}>
        {label}
      </span>
      {children}
    </div>
  );
}

export function TemplateFilters({
  filters,
  counts,
  categories,
  industries,
  collections,
  capabilities,
  shown,
  total,
  canSortByNewest,
  onChange,
  onSearch,
}: {
  filters: Filters;
  counts: ShelfCounts;
  categories: FilterOption[];
  industries: FilterOption[];
  collections: FilterOption[];
  capabilities: FilterOption[];
  shown: number;
  total: number;
  /** Whether the rows say when they went live (offers "Newest"). */
  canSortByNewest: boolean;
  onChange: (next: Filters) => void;
  /** The search box's own channel: the gallery filters at once and writes the URL a beat later. */
  onSearch: (q: string) => void;
}) {
  const intl = useIntl();
  const rowsId = useId();
  const [open, setOpen] = useState(false);
  const set = (patch: Partial<Filters>) => onChange({ ...filters, ...patch });
  const setKind = (kind: KindFilter) =>
    set({ kind, inTeam: kind === "single" ? filters.inTeam : "any" });
  const setInTeam = (inTeam: InTeamFilter) => set({ inTeam });
  const setFrom = (from: FromFilter) => set({ from });
  const active = hasActiveFilters(filters);
  const activeCount =
    Number(filters.from !== "all") +
    Number(filters.kind !== "all") +
    Number(filters.category !== null) +
    filters.industries.length +
    filters.collections.length +
    filters.capabilities.length;
  const sort: SortOrder =
    filters.sort === "newest" && !canSortByNewest ? "recommended" : filters.sort;

  return (
    <div data-testid="template-filters">
      <input
        type="search"
        value={filters.q}
        onChange={(event) => onSearch(event.target.value)}
        aria-label={intl.formatMessage({ id: "tpl.searchLabel" })}
        placeholder={intl.formatMessage({ id: "tpl.searchPlaceholder" })}
        style={{
          marginTop: "16px",
          width: "100%",
          boxSizing: "border-box",
          padding: "10px 14px",
          background: card,
          border: `var(--brand-bw-2-5) solid ${ink}`,
          borderRadius: "var(--brand-r-12)",
          boxShadow: "var(--brand-shadow-2-5)",
          fontFamily: "var(--brand-font-body)",
          fontWeight: 600,
          fontSize: "14px",
          color: ink,
        }}
      />

      <button
        type="button"
        className={styles.drawerToggle}
        aria-expanded={open}
        aria-controls={rowsId}
        onClick={() => setOpen((current) => !current)}
        style={{ ...smallButtonSty, marginTop: "12px", padding: "6px 14px", fontSize: "13px" }}
      >
        <FormattedMessage id="tpl.filter.toggle" values={{ count: activeCount }} />
      </button>

      <div id={rowsId} className={styles.rows} data-open={open ? "true" : "false"}>
        <RadioRow label={intl.formatMessage({ id: "tpl.filter.from" })}>
          <Chip tone="violet" radio on={filters.from === "all"} onClick={() => setFrom("all")}>
            <FormattedMessage id="tpl.filter.from.all" />
          </Chip>
          {SITES_IN_ORDER.map((site) => (
            <Chip
              key={site}
              tone="violet"
              radio
              on={filters.from === site}
              count={counts.from.sites.get(site) ?? 0}
              onClick={() => setFrom(site)}
            >
              {SITES[site].name}
            </Chip>
          ))}
        </RadioRow>

        <RadioRow label={intl.formatMessage({ id: "tpl.filter.kind" })}>
          <Chip tone="violet" radio on={filters.kind === "all"} onClick={() => setKind("all")}>
            <FormattedMessage id="tpl.filter.kind.all" />
          </Chip>
          <Chip
            tone="violet"
            radio
            on={filters.kind === "team"}
            count={counts.kind.team}
            onClick={() => setKind("team")}
          >
            <FormattedMessage id="tpl.filter.kind.team" />
          </Chip>
          <Chip
            tone="violet"
            radio
            on={filters.kind === "single"}
            count={counts.kind.single}
            onClick={() => setKind("single")}
          >
            <FormattedMessage id="tpl.filter.kind.single" />
          </Chip>
        </RadioRow>

        {filters.kind === "single" ? (
          <RadioRow label={intl.formatMessage({ id: "tpl.filter.inTeam" })}>
            <Chip
              tone="violet"
              radio
              on={filters.inTeam === "any"}
              onClick={() => setInTeam("any")}
            >
              <FormattedMessage id="tpl.filter.inTeam.any" />
            </Chip>
            <Chip
              tone="violet"
              radio
              on={filters.inTeam === "yes"}
              count={counts.inTeam.yes}
              onClick={() => setInTeam("yes")}
            >
              <FormattedMessage id="tpl.filter.inTeam.yes" />
            </Chip>
            <Chip
              tone="violet"
              radio
              on={filters.inTeam === "no"}
              count={counts.inTeam.no}
              onClick={() => setInTeam("no")}
            >
              <FormattedMessage id="tpl.filter.inTeam.no" />
            </Chip>
          </RadioRow>
        ) : null}

        <RadioRow label={intl.formatMessage({ id: "tpl.filter.category" })}>
          <Chip radio on={filters.category === null} onClick={() => set({ category: null })}>
            <FormattedMessage id="tpl.allCategories" />
          </Chip>
          {categories.map((option) => (
            <Chip
              key={option.key}
              radio
              on={filters.category === option.key}
              count={counts.categories.get(option.key) ?? 0}
              legacy={option.legacy}
              onClick={() => set({ category: option.key })}
            >
              {option.label}
            </Chip>
          ))}
        </RadioRow>

        {industries.length ? (
          <Row label={intl.formatMessage({ id: "tpl.filter.industry" })}>
            {industries.map((option) => (
              <Chip
                key={option.key}
                on={filters.industries.includes(option.key)}
                count={counts.industries.get(option.key) ?? 0}
                onClick={() => set({ industries: toggleValue(filters.industries, option.key) })}
              >
                {option.label}
              </Chip>
            ))}
          </Row>
        ) : null}

        {collections.length ? (
          <Row label={intl.formatMessage({ id: "tpl.filter.collections" })}>
            {collections.map((option) => (
              <Chip
                key={option.key}
                on={filters.collections.includes(option.key)}
                count={counts.collections.get(option.key) ?? 0}
                onClick={() => set({ collections: toggleValue(filters.collections, option.key) })}
              >
                {option.label}
              </Chip>
            ))}
          </Row>
        ) : null}

        {capabilities.length ? (
          <Row label={intl.formatMessage({ id: "tpl.filter.capabilities" })}>
            {capabilities.map((option) => (
              <Chip
                key={option.key}
                on={filters.capabilities.includes(option.key)}
                count={counts.capabilities.get(option.key) ?? 0}
                onClick={() => set({ capabilities: toggleValue(filters.capabilities, option.key) })}
              >
                {option.label}
              </Chip>
            ))}
          </Row>
        ) : null}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
          marginTop: "14px",
        }}
      >
        <output
          data-filter-count=""
          aria-live="polite"
          style={{ fontWeight: 700, fontSize: "13px", color: "var(--brand-muted-2)" }}
        >
          <FormattedMessage id="tpl.filter.showing" values={{ shown, total }} />
        </output>
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            fontFamily: "var(--brand-font-display)",
            fontWeight: 800,
            fontSize: "12px",
            color: "var(--brand-muted-2)",
          }}
        >
          <FormattedMessage id="tpl.sort" />
          <select
            value={sort}
            onChange={(event) => set({ sort: event.target.value as SortOrder })}
            style={{
              padding: "4px 8px",
              background: card,
              border: `var(--brand-bw-2) solid ${ink}`,
              borderRadius: "var(--brand-r-10)",
              fontFamily: "var(--brand-font-body)",
              fontWeight: 700,
              fontSize: "12.5px",
              color: ink,
            }}
          >
            <option value="recommended">
              {intl.formatMessage({ id: "tpl.sort.recommended" })}
            </option>
            <option value="title">{intl.formatMessage({ id: "tpl.sort.title" })}</option>
            {canSortByNewest ? (
              <option value="newest">{intl.formatMessage({ id: "tpl.sort.newest" })}</option>
            ) : null}
          </select>
        </label>
        {active ? (
          <button
            type="button"
            onClick={() => onChange(clearFilters(filters))}
            style={smallButtonSty}
          >
            <FormattedMessage id="tpl.filter.clear" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
